// src/components/react-dashboard/market_pulse/widgets/MarketPulseTrends.jsx
import React, { useState, useMemo, useEffect } from "react";
import dayjs from "dayjs";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Paper,
    Box,
    Checkbox,
    Typography,
    Button,
    TextField,
    CircularProgress,
    useTheme,
} from "@mui/material";
import { LocalizationProvider, DateRangePicker } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from "recharts";
import { DataGrid } from "@mui/x-data-grid";
import { useGetDailyTrendQuery } from "../../../../features/market_pulse/marketPulseApiSlice";
import { tokens } from "../../../../theme";

/* --------------------------- helpers --------------------------- */
const VISIBLE_PRODUCTS = ["BUSINESS", "COMPANY_WEEKLY", "CONSUMER", "CUSTOM_TOPIC"];

const buildColorMap = (labels) => {
    const step = 360 / labels.length;
    return Object.fromEntries(
        labels.map((lbl, idx) => [`${lbl}`, `hsl(${Math.round(idx * step)} 80% 55%)`]),
    );
};
const colorMap = buildColorMap(VISIBLE_PRODUCTS);

const CustomTooltip = ({ active, payload, label }) =>
    !active || !payload?.length ? null : (
        <Paper elevation={3} sx={{ p: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {dayjs(label).format("MMM D, YYYY")}
            </Typography>
            {payload.map((e) => (
                <Typography key={e.dataKey} sx={{ color: e.stroke, fontWeight: 600 }}>
                    {e.name}: {Number(e.value).toLocaleString()}
                </Typography>
            ))}
        </Paper>
    );

/* --------------------------- component ------------------------- */
const MarketPulseTrends = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    /* state ------------------------------------------------------- */
    const [visibleProducts, setVisibleProducts] = useState(VISIBLE_PRODUCTS);
    const MAX_DAYS = 180;
    const [pickerRange, setPickerRange] = useState([dayjs().subtract(29, "day"), dayjs()]);

    // debounce range
    const [range, setRange] = useState(pickerRange);
    useEffect(() => {
        const id = setTimeout(() => setRange(pickerRange), 400);
        return () => clearTimeout(id);
    }, [pickerRange]);

    const [b2bCSV, setB2bCSV] = useState("");
    const [b2cCSV, setB2cCSV] = useState("");

    /* helper ---------------------------------------------------- */
    const normCsv = (csv) =>
        csv
            .split(",")               // break into IDs
            .map((s) => s.trim().toLowerCase())  // trim + lowercase each
            .filter(Boolean)          // drop empties
            .join(",");

    /* data ------------------------------------------------------ */
    const [startD, endD] = range;

    const { data: trendResp = { rows: [] }, isFetching } =
        useGetDailyTrendQuery({
            start: startD.format("YYYY-MM-DD"),
            end: endD.format("YYYY-MM-DD"),
            products: visibleProducts.join(","),
            topics_b2b: b2bCSV ? normCsv(b2bCSV) : undefined,
            topics_b2c: b2cCSV ? normCsv(b2cCSV) : undefined,
        });

    /* reshape for chart + grid ----------------------------------- */
    const chartData = useMemo(() => {
        const byDay = {};
        trendResp.rows.forEach(({ DAY, PRODUCT, COUNT }) => {
            const d = dayjs(DAY).format("YYYY-MM-DD");
            if (!byDay[d]) byDay[d] = { id: d, day: d }; // id for DataGrid
            byDay[d][PRODUCT] = Number(COUNT);
        });
        return Object.values(byDay).sort((a, b) => a.day.localeCompare(b.day));
    }, [trendResp]);

    /* grid columns ------------------------------------------------ */
    const gridColumns = [
        { field: "day", headerName: "Date", minWidth: 110, valueFormatter: ({ value }) => dayjs(value).format("MMM D") },
        ...VISIBLE_PRODUCTS.map((p) => ({
            field: p,
            headerName: p.replace("_", " "),
            minWidth: 160,
            type: "number",
            valueFormatter: ({ value }) => (value ? value.toLocaleString() : ""),
        })),
    ];

    /* ------------------------- UI ------------------------------- */
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            {/* SINGLE ACCORDION (chart + daily list) ------------------------------ */}
            {/* MARKET-PULSE TRENDS (chart + daily list) --------------------------- */}
            <Accordion defaultExpanded sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h4" fontWeight="bold" >Market Pulse Trends</Typography>
                </AccordionSummary>

                <AccordionDetails>
                    {/* ─── DATE-RANGE SUMMARY & PICKER ─────────────────────────────── */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            flexWrap: "wrap",
                            mb: 2,
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {startD.format("MMM D YYYY")} – {endD.format("MMM D YYYY")}
                        </Typography>

                        <DateRangePicker
                            value={pickerRange}
                            onChange={(v) => v[0] && v[1] && setPickerRange(v)}
                            minDate={dayjs().subtract(MAX_DAYS, "day")}
                            maxDate={dayjs()}
                            disableFuture
                            slotProps={{ textField: { size: "small", sx: { maxWidth: 140 } } }}
                        />
                    </Box>

                    {/* ─── product toggles  filters ───────────────────────────────── */}
                    <Paper
                        variant="outlined"
                        sx={{ p: 2, mb: 2, display: "flex", flexWrap: "wrap", gap: 3, alignItems: "center" }}
                    >
                        {VISIBLE_PRODUCTS.map((p) => (
                            <Box key={p} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <Checkbox
                                    checked={visibleProducts.includes(p)}
                                    onChange={() =>
                                        setVisibleProducts((prev) =>
                                            prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p],
                                        )
                                    }
                                    sx={{ color: colorMap[p], "&.Mui-checked": { color: colorMap[p] } }}
                                />
                                <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>{p}</Typography>
                            </Box>
                        ))}

                        <Button
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: colors.primary[100], color: colors.primary[100] }}
                            onClick={() =>
                                setVisibleProducts((prev) =>
                                    prev.length === VISIBLE_PRODUCTS.length ? [] : VISIBLE_PRODUCTS,
                                )
                            }
                        >
                            {visibleProducts.length === VISIBLE_PRODUCTS.length ? "Deselect All" : "Select All"}
                        </Button>

                        {/* topic ID filters */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, ml: 3 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Filter Topics
                            </Typography>

                            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                                <TextField
                                    size="small"
                                    label="B2B IDs (CSV)"
                                    value={b2bCSV}
                                    onChange={(e) => setB2bCSV(e.target.value)}
                                    sx={{ minWidth: 220 }}
                                />
                                <TextField
                                    size="small"
                                    label="B2C IDs (CSV)"
                                    value={b2cCSV}
                                    onChange={(e) => setB2cCSV(e.target.value)}
                                    sx={{ minWidth: 220 }}
                                />
                            </Box>
                        </Box>
                    </Paper>

                    {/* ───────────────────────────  CHART  ─────────────────────────── */}
                    {isFetching ? (
                        <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
                            <CircularProgress size={48} sx={{ color: colors.primary[400] }} />
                        </Box>
                    ) : (
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={chartData} margin={{ left: 35, right: 10, top: 5, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="day" tick={{ fill: colors.primary[100] }} />
                                <YAxis
                                    tickFormatter={(v) => v.toLocaleString()}
                                    tick={{ fill: colors.primary[100] }}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ fontSize: "0.9rem" }} />

                                {visibleProducts.map((p) => (
                                    <Line
                                        key={p}
                                        type="monotone"
                                        dataKey={p}
                                        stroke={colorMap[p]}
                                        strokeWidth={2}
                                        dot={{ r: 6, fill: colorMap[p], stroke: "none" }}
                                        activeDot={{ r: 8 }}
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                    {/* ────────────────────  DAILY TOPIC LIST (collapsed)  ─────────── */}
                    <Accordion
                        defaultExpanded={false}
                        elevation={0}
                        sx={{
                            mt: 3,
                            "& .MuiAccordionSummary-root": { px: 0 },
                            "& .MuiAccordionDetails-root": { px: 0, pt: 1.5 },
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6" marginLeft={1}>Product Counts by Day</Typography>
                        </AccordionSummary>

                        <AccordionDetails>
                            <Paper
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    marginLeft: 2,
                                    marginRight: 2,
                                    background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[800]} 100%)`,
                                }}
                            >
                                {isFetching ? (
                                    <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
                                        <CircularProgress size={48} sx={{ color: colors.greenAccent[400] }} />
                                    </Box>
                                ) : (
                                    <DataGrid
                                        autoHeight
                                        rows={chartData}
                                        columns={gridColumns}
                                        pageSize={25}
                                        disableRowSelectionOnClick
                                        sx={{
                                            "& .MuiDataGrid-columnHeader": {
                                                fontSize: "0.9rem",
                                                fontWeight: 600,
                                                color: colors.primary[100],
                                            },
                                            "& .MuiDataGrid-cell": { fontSize: "0.85rem", color: colors.primary[100] },
                                            // soften grid lines on dark gradient
                                            "& .MuiDataGrid-root": { borderColor: colors.primary[700] },
                                            marginLeft: 1,
                                            marginRight: 1,
                                        }}
                                    />
                                )}
                            </Paper>
                        </AccordionDetails>
                    </Accordion>

                </AccordionDetails>
            </Accordion>
        </LocalizationProvider>
    );
};

export default MarketPulseTrends;
